import TagManager from 'react-gtm-module';
import {
  Currency,
  GA4AddPaymentInfo,
  GA4AddToCart,
  GA4BeginCheckout,
  GA4PaymentFailed,
  GTM_ID,
  GTMAddPaymentInfo,
  GTMPaymentFailed,
  GTMStartPayment,
  GtmAddToCart,
} from './constant';

/**
 * Fire add-to-cart conversion events for marketing/analytics platforms.
 *
 * Mirrors the legacy /views/Subscribe/SelectPackage.js dataLayer payload
 * shape so existing GTM tags continue to work without reconfiguration.
 * Failures are caught and logged so an analytics outage never breaks the
 * user-visible cart flow.
 *
 * Tags fired:
 *   - GTM custom `addToCart` event (workspace-defined trigger)
 *   - GTM `add_to_cart` GA4 standard ecommerce event
 *   - Facebook Pixel `AddToCart` standard event (NOT `Purchase` — the legacy
 *     code wrongly used `Purchase` on add-to-cart, inflating FB conversion
 *     counts)
 *
 * @param {object} params
 * @param {string} [params.exam='']            - Display exam name (e.g. "CFA Level I")
 * @param {string} [params.packageType='']     - Package label (e.g. "CFA Unlimited+ Package (All 3 Levels)")
 * @param {number} [params.value=0]            - Item value in USD
 * @param {string} [params.userName='']
 * @param {string} [params.userEmail='']
 * @param {string} [params.source='organic']
 * @param {string|null} [params.affiliateName=null]
 */
export const trackAddToCart = ({
  exam = '',
  packageType = '',
  value = 0,
  userName = '',
  userEmail = '',
  source = 'organic',
  affiliateName = null,
} = {}) => {
  try {
    const numericValue = Number(value) || 0;

    // GTM custom event — workspace tags (Bing UET, custom audiences, etc.)
    // listen for `addToCart`. Payload shape preserved from legacy code so
    // existing GTM triggers / variables keep matching.
    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GtmAddToCart,
        eventOptions: {
          addedPackage: {
            exam,
            packageType,
            price: numericValue,
          },
        },
        userName,
        userEmail,
        userExam: exam,
        source,
        affiliateName,
        price: numericValue,
        value: numericValue,
        currency: Currency,
      },
    });

    // GA4 standard ecommerce `add_to_cart` event
    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GA4AddToCart,
        eventOptions: {
          addedPackage: {
            exam,
            packageType,
            value: numericValue,
          },
        },
        currency: Currency,
        value: numericValue,
        items: [{ item_id: userEmail || exam, item_name: exam, price: numericValue }],
        userName,
        userEmail,
        userExam: exam,
        source,
        affiliateName,
        price: numericValue,
      },
    });

    // Facebook Pixel — correct standard `AddToCart` event.
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'AddToCart', {
        currency: Currency,
        value: numericValue,
        content_type: 'product',
        content_name: packageType || exam,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('trackAddToCart failed:', err && err.message);
  }
};

/**
 * Build the cart array shape used by the GTM/GA4 begin_checkout payloads.
 * Mirrors the legacy Cart.js array shape so existing GTM tags pick it up.
 *
 * @param {Array} cartItems - rows from /checkout_v1/getUserCart
 * @returns {Array<{ exam: string, packageType: string, price: number }>}
 */
const buildCartArray = (cartItems = []) => {
  if (!Array.isArray(cartItems)) return [];
  return cartItems.map((item) => {
    const extras = Array.isArray(item && item.extraData) ? item.extraData : [];
    const extrasTotal = extras.reduce((sum, ex) => sum + (Number(ex.price) || 0), 0);
    return {
      exam: (item && item.exam_name) || '',
      packageType: (item && item.package_type) || '',
      price: (Number(item && item.price) || 0) + extrasTotal,
    };
  });
};

/**
 * Fire begin-checkout conversion events.
 *
 * Trigger: user clicks "Proceed to Checkout" on the cart page (i.e., is
 * leaving Cart.js for Checkout.js). Mirrors the legacy Cart.js dataLayer
 * payload shape so existing GTM tags continue to match.
 *
 * Tags fired:
 *   - GTM custom `startPayment` event (workspace-defined trigger)
 *   - GTM `begin_checkout` GA4 standard ecommerce event
 *   - Facebook Pixel `InitiateCheckout` standard event
 *
 * @param {object} params
 * @param {Array}  [params.cartItems=[]]    - rows from /checkout_v1/getUserCart
 * @param {number} [params.total=0]         - cart total in USD (post-discount)
 * @param {string} [params.paymentType='']  - 'stripe' | 'paypal' | '' if not yet selected
 * @param {string} [params.userName='']
 * @param {string} [params.userEmail='']
 * @param {string} [params.source='organic']
 * @param {string|null} [params.affiliateName=null]
 */
export const trackBeginCheckout = ({
  cartItems = [],
  total = 0,
  paymentType = '',
  userName = '',
  userEmail = '',
  source = 'organic',
  affiliateName = null,
} = {}) => {
  try {
    const cart = buildCartArray(cartItems);
    const value = Number(total) || 0;

    // GTM custom event
    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GTMStartPayment,
        eventOptions: {
          paymentOption: paymentType,
          cart,
          cartTotal: value,
        },
        userName,
        userEmail,
        source,
        affiliateName,
        currency: Currency,
        value,
        price: value,
      },
    });

    // GA4 standard ecommerce begin_checkout
    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GA4BeginCheckout,
        currency: Currency,
        value,
        items: cart,
        userName,
        userEmail,
        source,
      },
    });

    // Facebook Pixel — standard `InitiateCheckout` event
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout', {
        currency: Currency,
        value,
        num_items: cart.length,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('trackBeginCheckout failed:', err && err.message);
  }
};

/**
 * Fire add-payment-info conversion events.
 *
 * Trigger: user has provided valid payment information on /checkout (Stripe
 * has returned a valid token). Fires BEFORE the /createOrder backend call,
 * so it captures intent regardless of whether the backend transaction
 * succeeds or is rejected.
 *
 * Tags fired:
 *   - GTM custom `addPaymentInfo` event
 *   - GTM `add_payment_info` GA4 standard ecommerce event
 *   - Facebook Pixel `AddPaymentInfo` standard event
 *
 * @param {object} params
 * @param {Array}  [params.cartItems=[]]
 * @param {number} [params.total=0]
 * @param {string} [params.paymentType='stripe']
 * @param {string} [params.userName='']
 * @param {string} [params.userEmail='']
 * @param {string} [params.source='organic']
 * @param {string|null} [params.affiliateName=null]
 */
export const trackAddPaymentInfo = ({
  cartItems = [],
  total = 0,
  paymentType = 'stripe',
  userName = '',
  userEmail = '',
  source = 'organic',
  affiliateName = null,
} = {}) => {
  try {
    const cart = buildCartArray(cartItems);
    const value = Number(total) || 0;

    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GTMAddPaymentInfo,
        eventOptions: {
          paymentOption: paymentType,
          cart,
          cartTotal: value,
        },
        userName,
        userEmail,
        source,
        affiliateName,
        currency: Currency,
        value,
        price: value,
      },
    });

    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GA4AddPaymentInfo,
        currency: Currency,
        value,
        items: cart,
        payment_type: paymentType,
        userName,
        userEmail,
        source,
      },
    });

    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'AddPaymentInfo', {
        currency: Currency,
        value,
        num_items: cart.length,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('trackAddPaymentInfo failed:', err && err.message);
  }
};

/**
 * Fire payment-failed diagnostic events.
 *
 * Trigger: any failure during /checkout payment submission. Fires once per
 * failed attempt (no idempotency guard inside the helper — caller controls
 * cadence).
 *
 * Tags fired:
 *   - GTM custom `paymentFailed` event with `reason` + `errorMessage`
 *   - GTM `payment_failed` custom GA4 event with same fields
 *   - Facebook Pixel `trackCustom` `PaymentFailed` (custom — only if your FB
 *     workspace whitelists it; otherwise silently skipped by FB)
 *
 * Use the `reason` field to bucket failure types in GA4:
 *   - 'card_validation' — Stripe.js token creation failed (declined card,
 *      bad CVC, expired, etc.)
 *   - 'backend_rejection' — /createOrder returned success: false
 *   - 'exception' — uncaught JS exception during the submit handler
 *
 * @param {object} params
 * @param {string} [params.reason='unknown']        - Bucket label
 * @param {string} [params.errorMessage='']         - Human-readable error
 * @param {Array}  [params.cartItems=[]]
 * @param {number} [params.total=0]
 * @param {string} [params.paymentType='stripe']
 * @param {string} [params.userName='']
 * @param {string} [params.userEmail='']
 * @param {string} [params.source='organic']
 * @param {string|null} [params.affiliateName=null]
 */
export const trackPaymentFailed = ({
  reason = 'unknown',
  errorMessage = '',
  cartItems = [],
  total = 0,
  paymentType = 'stripe',
  userName = '',
  userEmail = '',
  source = 'organic',
  affiliateName = null,
} = {}) => {
  try {
    const cart = buildCartArray(cartItems);
    const value = Number(total) || 0;

    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GTMPaymentFailed,
        eventOptions: {
          reason,
          errorMessage,
          paymentOption: paymentType,
          cart,
          cartTotal: value,
        },
        userName,
        userEmail,
        source,
        affiliateName,
        currency: Currency,
        value,
        reason,
        errorMessage,
      },
    });

    TagManager.initialize({
      gtmId: GTM_ID,
      dataLayer: {
        event: GA4PaymentFailed,
        currency: Currency,
        value,
        items: cart,
        payment_type: paymentType,
        reason,
        error_message: errorMessage,
        userName,
        userEmail,
        source,
      },
    });

    // FB Pixel custom event — fbq supports `trackCustom` for non-standard events.
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('trackCustom', 'PaymentFailed', {
        currency: Currency,
        value,
        reason,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('trackPaymentFailed failed:', err && err.message);
  }
};



// WEBPACK FOOTER //
// ./src/common/analytics.js